import { useEffect, useState } from 'react';
import { Loader2, CheckCircle2, AlertCircle, ArrowRight } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { apiCall, formatCents } from '@/lib/api';

interface Plan {
  id: string;
  name: string;
  description: string;
  monthly_cents: number;
  youth: boolean;
}

interface PlansResp { plans: Plan[]; stripe_configured: boolean; org: { id: string; slug: string; name: string } | null }

export default function PublicSignup() {
  const [plans, setPlans] = useState<Plan[] | null>(null);
  const [stripeOk, setStripeOk] = useState(true);
  const [orgName, setOrgName] = useState<string>('our club');
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [selected, setSelected] = useState<Plan | null>(null);

  useEffect(() => {
    apiCall<PlansResp>('/v1/signup/plans')
      .then((r) => { setPlans(r.plans); setStripeOk(r.stripe_configured); if (r.org?.name) setOrgName(r.org.name); })
      .catch((e) => setLoadErr(e instanceof Error ? e.message : 'Failed'));
  }, []);

  if (loadErr) {
    return <Centered><Card className="border-destructive/40 bg-destructive/5"><CardContent className="py-4 text-sm text-destructive flex items-center gap-2"><AlertCircle className="h-4 w-4" /> {loadErr}</CardContent></Card></Centered>;
  }
  if (!plans) {
    return <Centered><div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading membership options…</div></Centered>;
  }
  if (!stripeOk) {
    return <Centered><Card><CardContent className="py-6 text-sm">Online signup is not available right now. Please <a href="mailto:info@unitedwrestlingclub.com" className="underline">email us</a> to join.</CardContent></Card></Centered>;
  }

  if (selected) {
    return <Centered wide><SignupForm plan={selected} onBack={() => setSelected(null)} /></Centered>;
  }

  return (
    <Centered wide>
      <div className="space-y-6">
        <header className="text-center space-y-2">
          <h1 className="text-3xl font-bold">Join {orgName}</h1>
          <p className="text-sm text-muted-foreground">Pick the plan that fits. Monthly subscription, cancel anytime.</p>
        </header>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {plans.map((p) => (
            <button
              key={p.id}
              onClick={() => setSelected(p)}
              className="text-left rounded-lg border bg-card hover:border-primary hover:shadow-sm transition-all p-5 space-y-2 group"
            >
              <div className="flex items-baseline justify-between">
                <span className="font-semibold">{p.name}</span>
                {p.youth && <span className="text-xs px-1.5 py-0.5 rounded bg-muted text-muted-foreground">youth</span>}
              </div>
              <div className="text-2xl font-bold tabular-nums">{formatCents(p.monthly_cents)}<span className="text-sm font-normal text-muted-foreground">/mo</span></div>
              <p className="text-xs text-muted-foreground">{p.description}</p>
              <div className="pt-2 text-xs text-primary opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1">
                Choose <ArrowRight className="h-3 w-3" />
              </div>
            </button>
          ))}
        </div>

        <p className="text-xs text-muted-foreground text-center">
          Payment is handled securely by Stripe. You'll be redirected to Stripe Checkout to enter card details.
        </p>
      </div>
    </Centered>
  );
}

function SignupForm({ plan, onBack }: { plan: Plan; onBack: () => void }) {
  const [athleteName, setAthleteName] = useState('');
  const [athleteEmail, setAthleteEmail] = useState('');
  const [athletePhone, setAthletePhone] = useState('');
  const [guardianName, setGuardianName] = useState('');
  const [guardianEmail, setGuardianEmail] = useState('');
  const [guardianPhone, setGuardianPhone] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setErr(null);
    try {
      const r = await apiCall<{ url: string }>('/v1/signup', {
        method: 'POST',
        body: {
          plan_id: plan.id,
          athlete_name: athleteName.trim(),
          athlete_email: athleteEmail.trim() || null,
          athlete_phone: athletePhone.trim() || null,
          guardian_name: plan.youth ? guardianName.trim() : null,
          guardian_email: plan.youth ? guardianEmail.trim() : null,
          guardian_phone: plan.youth ? guardianPhone.trim() : null,
        },
      });
      window.location.href = r.url;
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Signup failed');
      setSubmitting(false);
    }
  }

  return (
    <div className="space-y-4">
      <button onClick={onBack} className="text-sm text-muted-foreground hover:text-foreground">← Back to plans</button>

      <Card>
        <CardContent className="py-5 space-y-1">
          <h2 className="text-lg font-semibold">{plan.name}</h2>
          <div className="text-xl font-bold tabular-nums">{formatCents(plan.monthly_cents)}<span className="text-sm font-normal text-muted-foreground">/month</span></div>
          <p className="text-xs text-muted-foreground">{plan.description}</p>
        </CardContent>
      </Card>

      <form onSubmit={submit}>
        <Card>
          <CardContent className="py-5 space-y-4">
            <section className="space-y-3">
              <h3 className="text-sm font-medium">Athlete</h3>
              <Field label="Full name *">
                <input value={athleteName} onChange={(e) => setAthleteName(e.target.value)} required className="w-full border rounded px-3 py-2 text-sm" />
              </Field>
              <div className="grid sm:grid-cols-2 gap-3">
                <Field label={plan.youth ? 'Email (optional)' : 'Email *'}>
                  <input type="email" value={athleteEmail} onChange={(e) => setAthleteEmail(e.target.value)} required={!plan.youth} className="w-full border rounded px-3 py-2 text-sm" />
                </Field>
                <Field label="Phone (optional)">
                  <input value={athletePhone} onChange={(e) => setAthletePhone(e.target.value)} className="w-full border rounded px-3 py-2 text-sm" />
                </Field>
              </div>
            </section>

            {plan.youth && (
              <section className="space-y-3 pt-2 border-t">
                <h3 className="text-sm font-medium">Parent / Guardian</h3>
                <Field label="Guardian name *">
                  <input value={guardianName} onChange={(e) => setGuardianName(e.target.value)} required className="w-full border rounded px-3 py-2 text-sm" />
                </Field>
                <div className="grid sm:grid-cols-2 gap-3">
                  <Field label="Guardian email *">
                    <input type="email" value={guardianEmail} onChange={(e) => setGuardianEmail(e.target.value)} required className="w-full border rounded px-3 py-2 text-sm" />
                  </Field>
                  <Field label="Guardian phone (optional)">
                    <input value={guardianPhone} onChange={(e) => setGuardianPhone(e.target.value)} className="w-full border rounded px-3 py-2 text-sm" />
                  </Field>
                </div>
                <p className="text-xs text-muted-foreground">Billing receipts go to the guardian email.</p>
              </section>
            )}

            <section className="pt-2 border-t text-xs text-muted-foreground space-y-1">
              <p className="flex items-start gap-2"><CheckCircle2 className="h-3.5 w-3.5 shrink-0 mt-0.5 text-emerald-600" /> Monthly subscription, cancel anytime from your account.</p>
              <p className="flex items-start gap-2"><CheckCircle2 className="h-3.5 w-3.5 shrink-0 mt-0.5 text-emerald-600" /> You'll sign the gym waiver after your first check-in.</p>
              <p className="flex items-start gap-2"><CheckCircle2 className="h-3.5 w-3.5 shrink-0 mt-0.5 text-emerald-600" /> Card details are entered securely on Stripe.</p>
            </section>

            {err && <p className="text-sm text-destructive">{err}</p>}

            <div className="pt-2">
              <Button type="submit" className="w-full" disabled={submitting}>
                {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                Continue to payment
              </Button>
            </div>
          </CardContent>
        </Card>
      </form>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block text-sm">
      <span className="block text-xs text-muted-foreground mb-1">{label}</span>
      {children}
    </label>
  );
}

function Centered({ children, wide }: { children: React.ReactNode; wide?: boolean }) {
  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className={wide ? 'w-full max-w-3xl' : 'w-full max-w-md'}>{children}</div>
    </div>
  );
}
